import React from 'react';
import classNames from 'classnames';

import NamedLink from '../NamedLink/NamedLink';

import css from './MarketingSections.module.css';

/**
 * Building blocks for Drivelo's coded marketing pages (homepage, Become an instructor, …).
 * They share one look: a photo hero, tinted and white sections, cards, numbered steps,
 * checklists, a deep-teal panel and an FAQ accordion. Copy is passed in already translated.
 */

const ICON_PATHS = {
  shield: ['M12 3l7 3v5c0 4.5-3 8.4-7 10-4-1.6-7-5.5-7-10V6l7-3z', 'M9 12l2 2 4-4'],
  receipt: ['M6 3h12v18l-3-2-3 2-3-2-3 2V3z', 'M9 8h6', 'M9 12h6', 'M9 16h3'],
  car: [
    'M4 16v-4l2-5a2 2 0 0 1 1.9-1.4h8.2A2 2 0 0 1 18 7l2 5v4',
    'M3 16h18v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1v-1H7v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
    'M4 12h16',
    'M7.5 14.5h1',
    'M15.5 14.5h1',
  ],
  star: ['M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z'],
  percent: [
    'M19 5L5 19',
    'M6.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
    'M17.5 20a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  ],
  calendar: ['M4 6h16v14H4z', 'M4 10h16', 'M8 3v4', 'M16 3v4', 'M9 14l2 2 4-4'],
  wallet: ['M3 7h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H3z', 'M3 7l12-4v4', 'M16 13.5h2'],
  check: ['M5 12.5l4.5 4.5L19 7.5'],
  cross: ['M6 6l12 12', 'M18 6L6 18'],
  chevron: ['M6 9l6 6 6-6'],
  arrow: ['M5 12h14', 'M13 6l6 6-6 6'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M20 20l-4-4'],
  pin: [
    'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z',
    'M12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  ],
};

/**
 * Line icon drawn with the current text colour. Decorative only.
 *
 * @param {Object} props
 * @param {string} props.name one of: shield, receipt, car, star, percent, calendar, wallet,
 * check, cross, chevron, arrow, search, pin
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingIcon = props => {
  const { name, className } = props;
  return (
    <svg
      className={classNames(css.icon, className)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON_PATHS[name].map(d => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
};

/**
 * Responsive, decorative photo from Console image variants. Renders nothing without variants.
 *
 * @param {Object} props
 * @param {Array<{ url: string, width: number }>} props.variants narrowest first
 * @param {string} props.sizes
 * @param {boolean?} props.eager load immediately (above the fold)
 * @param {string?} props.className
 * @returns {JSX.Element|null}
 */
export const MarketingPhoto = props => {
  const { variants, sizes, eager, className } = props;
  if (!variants?.length) {
    return null;
  }
  const fallback = variants.find(v => v.width >= 1200) || variants[variants.length - 1];
  return (
    <img
      className={className}
      src={fallback.url}
      srcSet={variants.map(v => `${v.url} ${v.width}w`).join(', ')}
      sizes={sizes}
      alt=""
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
};

/**
 * Button-styled link: to a named route, or to an in-page anchor with `href`.
 *
 * @param {Object} props
 * @param {string?} props.name route name
 * @param {Object?} props.params route params
 * @param {string?} props.href in-page link such as '#learners', used instead of a route
 * @param {'primary'|'light'|'outline'|'ghost'} [props.variant] light = white button and
 * outline = white-bordered button, both for dark backgrounds; ghost = quiet bordered button for
 * light backgrounds
 * @param {string?} props.className
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MarketingButton = props => {
  const { name, params, href, variant = 'primary', className, children } = props;
  const classes = classNames(
    css.button,
    {
      [css.buttonLight]: variant === 'light',
      [css.buttonOutline]: variant === 'outline',
      [css.buttonGhost]: variant === 'ghost',
    },
    className
  );
  return href ? (
    <a href={href} className={classes}>
      {children}
    </a>
  ) : (
    <NamedLink name={name} params={params} className={classes}>
      {children}
    </NamedLink>
  );
};

/**
 * Row of buttons, wrapping on small screens.
 *
 * @param {Object} props
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MarketingActions = props => <div className={css.actions}>{props.children}</div>;

/**
 * Full-width photo hero with the page's H1.
 *
 * @param {Object} props
 * @param {string} props.id id for the heading
 * @param {Array} props.photo image variants; a deep-teal background is used without one
 * @param {ReactNode} props.title
 * @param {ReactNode?} props.subtitle
 * @param {ReactNode?} props.children search box, buttons or points under the subtitle
 * @returns {JSX.Element}
 */
export const MarketingHero = props => {
  const { id, photo, title, subtitle, children } = props;
  return (
    <section className={css.hero} aria-labelledby={id}>
      <MarketingPhoto className={css.heroPhoto} variants={photo} sizes="100vw" eager />
      <div className={css.heroInner}>
        <h1 id={id} className={css.heroTitle}>
          {title}
        </h1>
        {subtitle ? <p className={css.heroSubtitle}>{subtitle}</p> : null}
        {children}
      </div>
    </section>
  );
};

/**
 * Short points with check marks, for the hero.
 *
 * @param {Object} props
 * @param {Array<{ key: string, text: ReactNode }>} props.items
 * @returns {JSX.Element}
 */
export const MarketingHeroPoints = props => (
  <ul className={css.heroPoints}>
    {props.items.map(({ key, text }) => (
      <li key={key} className={css.heroPoint}>
        <MarketingIcon name="check" className={css.heroPointIcon} />
        {text}
      </li>
    ))}
  </ul>
);

/**
 * Page section with a centred content column.
 *
 * @param {Object} props
 * @param {string} props.labelledBy id of the section's heading
 * @param {string?} props.anchorId id for in-page links to this section
 * @param {boolean?} props.tinted warm grey background instead of white
 * @param {boolean?} props.flushTop no top padding, to follow a section of the same colour
 * @param {string?} props.contentClassName
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MarketingSection = props => {
  const { labelledBy, anchorId, tinted, flushTop, contentClassName, children } = props;
  return (
    <section
      id={anchorId}
      className={classNames(css.section, {
        [css.sectionTinted]: tinted,
        [css.sectionFlushTop]: flushTop,
      })}
      aria-labelledby={labelledBy}
    >
      <div className={classNames(css.content, contentClassName)}>{children}</div>
    </section>
  );
};

/**
 * Section heading (H2) with an optional eyebrow and subtitle.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {ReactNode} props.title
 * @param {ReactNode?} props.eyebrow
 * @param {ReactNode?} props.subtitle
 * @param {'center'|'left'} [props.align]
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingSectionHeader = props => {
  const { id, title, eyebrow, subtitle, align = 'center', className } = props;
  return (
    <header
      className={classNames(
        css.sectionHeader,
        { [css.sectionHeaderLeft]: align === 'left' },
        className
      )}
    >
      {eyebrow ? <p className={css.eyebrow}>{eyebrow}</p> : null}
      <h2 id={id} className={css.sectionTitle}>
        {title}
      </h2>
      {subtitle ? <p className={css.sectionSubtitle}>{subtitle}</p> : null}
    </header>
  );
};

/**
 * Grid of white cards with an optional icon, status tags, a title, text and a bullet list.
 *
 * @param {Object} props
 * @param {Array<Object>} props.items { key, icon?, tags?, title, text?, list? }, where tags are
 * `{ key, text, tone }` with tone 'done' (teal tick) or 'community' (star), and list items are
 * `{ key, text }`
 * @param {2|3|4} [props.columns] columns on large screens
 * @returns {JSX.Element}
 */
export const MarketingCards = props => {
  const { items, columns = 4 } = props;
  return (
    <ul
      className={classNames(css.cards, {
        [css.cardsThree]: columns === 3,
        [css.cardsTwo]: columns === 2,
      })}
    >
      {items.map(({ key, icon, tags, title, text, list }) => (
        <li key={key} className={css.card}>
          {icon ? (
            <span className={css.cardIcon}>
              <MarketingIcon name={icon} className={css.cardIconSvg} />
            </span>
          ) : null}
          {tags?.length ? (
            <ul className={css.cardTags}>
              {tags.map(({ key: tagKey, text: tagText, tone }) => (
                <li
                  key={tagKey}
                  className={classNames(css.cardTag, {
                    [css.cardTagCommunity]: tone === 'community',
                  })}
                >
                  <MarketingIcon
                    name={tone === 'community' ? 'star' : 'check'}
                    className={css.cardTagIcon}
                  />
                  {tagText}
                </li>
              ))}
            </ul>
          ) : null}
          <h3 className={css.itemTitle}>{title}</h3>
          {text ? <p className={css.itemText}>{text}</p> : null}
          {list?.length ? (
            <ul className={css.cardList}>
              {list.map(({ key: itemKey, text: itemText }) => (
                <li key={itemKey}>{itemText}</li>
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
};

/**
 * Plain paragraphs at a readable line length.
 *
 * @param {Object} props
 * @param {Array<{ key: string, text: ReactNode }>} props.paragraphs
 * @param {'left'|'center'} [props.align]
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingProse = props => {
  const { paragraphs, align = 'left', className } = props;
  return (
    <div className={classNames(css.prose, { [css.proseCentred]: align === 'center' }, className)}>
      {paragraphs.map(({ key, text }) => (
        <p key={key}>{text}</p>
      ))}
    </div>
  );
};

/**
 * A short aside in a soft box, e.g. an exception or a tip.
 *
 * @param {Object} props
 * @param {ReactNode?} props.title
 * @param {ReactNode} props.children
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingNote = props => {
  const { title, children, className } = props;
  return (
    <aside className={classNames(css.note, className)}>
      {title ? <p className={css.noteTitle}>{title}</p> : null}
      <div className={css.noteText}>{children}</div>
    </aside>
  );
};

/**
 * White bordered box for content that sits on a tinted section (lists of costs, checklists).
 *
 * @param {Object} props
 * @param {ReactNode} props.children
 * @param {ReactNode?} props.footnote small text under a divider at the bottom
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingBox = props => {
  const { children, footnote, className } = props;
  return (
    <div className={classNames(css.box, className)}>
      {children}
      {footnote ? <p className={css.boxFootnote}>{footnote}</p> : null}
    </div>
  );
};

/**
 * A list of costs with an optional highlighted total row.
 *
 * @param {Object} props
 * @param {Array<{ key: string, label: ReactNode, amount: ReactNode, note: ReactNode? }>} props.rows
 * @param {{ label: ReactNode, amount: ReactNode }?} props.total
 * @returns {JSX.Element}
 */
export const MarketingCostList = props => {
  const { rows, total } = props;
  return (
    <dl className={css.costList}>
      {rows.map(({ key, label, amount, note }) => (
        <div key={key} className={css.costRow}>
          <dt className={css.costLabel}>
            {label}
            {note ? <span className={css.costNote}>{note}</span> : null}
          </dt>
          <dd className={css.costAmount}>{amount}</dd>
        </div>
      ))}
      {total ? (
        <div className={classNames(css.costRow, css.costTotal)}>
          <dt className={css.costLabel}>{total.label}</dt>
          <dd className={css.costAmount}>{total.amount}</dd>
        </div>
      ) : null}
    </dl>
  );
};

/**
 * Numbered steps: a row of up to three on wider screens, or a vertical timeline.
 *
 * @param {Object} props
 * @param {Array<{ key: string, title: ReactNode, text: ReactNode }>} props.items
 * @param {'row'|'timeline'} [props.layout]
 * @returns {JSX.Element}
 */
export const MarketingSteps = props => {
  const { items, layout = 'row' } = props;
  const isTimeline = layout === 'timeline';
  return (
    <ol className={isTimeline ? css.timeline : css.steps}>
      {items.map(({ key, title, text }, index) => (
        <li key={key} className={isTimeline ? css.timelineStep : css.step}>
          <span className={css.stepNumber} aria-hidden="true">
            {index + 1}
          </span>
          <div className={css.stepBody}>
            <h3 className={css.itemTitle}>{title}</h3>
            <p className={css.itemText}>{text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
};

/**
 * List with teal check marks (or crosses).
 *
 * @param {Object} props
 * @param {Array<{ key: string, text: ReactNode }>} props.items
 * @param {'check'|'cross'} [props.icon]
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingChecklist = props => {
  const { items, icon = 'check', className } = props;
  return (
    <ul className={classNames(css.checklist, className)}>
      {items.map(({ key, text }) => (
        <li key={key} className={css.checklistItem}>
          <span
            className={classNames(css.checklistMark, {
              [css.checklistMarkMuted]: icon === 'cross',
            })}
          >
            <MarketingIcon name={icon} className={css.checklistIcon} />
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  );
};

/**
 * Photo beside content. The photo comes first on phones.
 *
 * @param {Object} props
 * @param {Array} props.photo image variants; content takes the full width without one
 * @param {boolean?} props.reverse photo on the right on wider screens
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MarketingSplit = props => {
  const { photo, reverse, children } = props;
  const hasPhoto = photo?.length > 0;
  return (
    <div
      className={classNames(css.split, {
        [css.splitWithPhoto]: hasPhoto,
        [css.splitReverse]: reverse,
      })}
    >
      <MarketingPhoto
        className={css.splitPhoto}
        variants={photo}
        sizes="(min-width: 1024px) 528px, 100vw"
      />
      <div className={css.splitBody}>{children}</div>
    </div>
  );
};

/**
 * Deep-teal call-to-action panel, with an optional photo beside it.
 *
 * @param {Object} props
 * @param {string} props.id id for the heading
 * @param {Array?} props.photo image variants; the panel is centred without one
 * @param {ReactNode?} props.eyebrow
 * @param {ReactNode} props.title
 * @param {ReactNode?} props.text
 * @param {ReactNode} props.action usually a MarketingButton
 * @param {boolean?} props.spacedTop add top padding, when it follows a tinted section
 * @returns {JSX.Element}
 */
export const MarketingPanel = props => {
  const { id, photo, eyebrow, title, text, action, spacedTop } = props;
  const hasPhoto = photo?.length > 0;
  return (
    <section
      className={classNames(css.panelSection, { [css.panelSectionSpaced]: spacedTop })}
      aria-labelledby={id}
    >
      <div className={classNames(css.panel, hasPhoto ? css.panelWithPhoto : css.panelCentred)}>
        <MarketingPhoto
          className={css.panelPhoto}
          variants={photo}
          sizes="(min-width: 1024px) 528px, 100vw"
        />
        <div className={css.panelBody}>
          {eyebrow ? <p className={css.panelEyebrow}>{eyebrow}</p> : null}
          <h2 id={id} className={css.panelTitle}>
            {title}
          </h2>
          {text ? <p className={css.panelText}>{text}</p> : null}
          {action}
        </div>
      </div>
    </section>
  );
};

/**
 * Questions as an accordion, beside the section heading on wider screens.
 *
 * @param {Object} props
 * @param {string} props.id id for the heading
 * @param {ReactNode} props.title
 * @param {ReactNode?} props.subtitle
 * @param {ReactNode?} props.link e.g. a link to the full FAQ
 * @param {Array<{ key: string, question: string, answer: string }>} props.items
 * @param {boolean?} props.tinted
 * @returns {JSX.Element}
 */
export const MarketingFaq = props => {
  const { id, title, subtitle, link, items, tinted = true } = props;
  return (
    <MarketingSection labelledBy={id} tinted={tinted} contentClassName={css.faqLayout}>
      <div>
        <MarketingSectionHeader
          id={id}
          title={title}
          subtitle={subtitle}
          align="left"
          className={css.faqHeader}
        />
        {link}
      </div>
      <MarketingAccordion items={items} />
    </MarketingSection>
  );
};

/**
 * Collapsible questions. A string answer is shown as one paragraph; any other node (e.g.
 * rendered markdown) is shown as it is.
 *
 * @param {Object} props
 * @param {Array<{ key: string, question: ReactNode, answer: ReactNode }>} props.items
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
export const MarketingAccordion = props => {
  const { items, className } = props;
  return (
    <div className={classNames(css.faqList, className)}>
      {items.map(({ key, question, answer }) => (
        <details key={key} className={css.faqItem}>
          <summary className={css.faqQuestion}>
            <span>{question}</span>
            <MarketingIcon name="chevron" className={css.faqChevron} />
          </summary>
          <div className={css.faqAnswer}>
            {typeof answer === 'string' ? <p>{answer}</p> : answer}
          </div>
        </details>
      ))}
    </div>
  );
};

/**
 * Teal text link with an arrow.
 *
 * @param {Object} props
 * @param {string} props.name route name
 * @param {Object?} props.params
 * @param {ReactNode} props.children
 * @returns {JSX.Element}
 */
export const MarketingTextLink = props => {
  const { name, params, children } = props;
  return (
    <NamedLink name={name} params={params} className={css.textLink}>
      {children}
      <MarketingIcon name="arrow" className={css.textLinkIcon} />
    </NamedLink>
  );
};
